import React from "react";
import { motion } from "framer-motion";
import { BOOK_COVERS, BOOK_FORMATS, AMAZON_AUTHOR_URL } from "../lib/content";
import { withAffiliate } from "../lib/affiliate";
import FlipBookCover from "./FlipBookCover";
import { BookOpen, Star, ExternalLink, RotateCcw } from "lucide-react";

const FORMAT_ICON = {
  Paperback: "📘",
  Hardcover: "📕",
  Kindle: "⚡",
};

const BookSeries = ({ books, loading }) => {
  return (
    <section
      id="books"
      data-testid="books-section"
      className="relative py-28 md:py-40 bg-enchanted"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <span className="font-accent text-xl text-amber-300 tracking-wide">
            The Book Series
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mt-3 leading-tight">
            Tales from <span className="italic text-gradient-gold">Miara Mountain</span>
          </h2>
          <div className="section-divider mt-6" />
          <p className="mt-6 text-emerald-50/75 max-w-2xl mx-auto text-lg">
            The enchanted collection by Sybelle Cheesman — five volumes of Miara
            Mountain, plus a companion tale from the Klay Realm of Gods.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 max-w-5xl mx-auto">
          {(books || []).map((book, idx) => {
            const firstFormat = BOOK_FORMATS[book.id]?.[0];
            return (
              <motion.article
                key={book.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8, delay: idx * 0.12 }}
                className="group relative"
                data-testid={`book-card-${book.id}`}
              >
                <div className="relative">
                  <FlipBookCover
                    cover={BOOK_COVERS[book.id]}
                    title={book.title}
                    label={book.subtitle?.split("—")[1]?.trim() || book.title}
                    synopsis={book.synopsis}
                    year={book.year}
                    testId={`book-flip-${book.id}`}
                  />
                </div>

                <div className="mt-7">
                  <h3
                    className="font-heading text-2xl md:text-3xl text-white leading-snug"
                    data-testid={`book-title-${book.id}`}
                  >
                    {book.title}
                  </h3>
                  <div className="font-accent text-amber-300 text-lg mt-1">
                    {book.subtitle?.split("—")[1]?.trim()}
                  </div>
                  <div className="flex items-center gap-1.5 mt-3 text-amber-400">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={14} fill="currentColor" stroke="none" />
                    ))}
                    <span className="text-xs text-emerald-50/60 ml-1">
                      Ages 6–12
                    </span>
                  </div>
                  <p className="mt-4 text-emerald-50/60 text-sm italic flex items-center gap-1.5" data-testid={`book-flip-hint-${book.id}`}>
                    <RotateCcw size={13} className="text-amber-300" />
                    Flip the book to read the back cover
                  </p>

                  {firstFormat && (
                    <div className="mt-6 flex items-center justify-between gap-4">
                      <span className="font-heading text-2xl text-gradient-gold">
                        from ${Number(firstFormat.price).toFixed(2)}
                      </span>
                      <a
                        href={withAffiliate(firstFormat.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-gold !py-2.5 !px-5 text-sm"
                        data-testid={`buy-book-${book.id}-button`}
                      >
                        <BookOpen size={16} />
                        Buy on Amazon
                      </a>
                    </div>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>

        <div className="text-center mt-16">
          <a
            href={withAffiliate(AMAZON_AUTHOR_URL)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost"
            data-testid="books-amazon-store-link"
          >
            <ExternalLink size={16} />
            Visit Sybelle&apos;s Amazon Author Store
          </a>
        </div>

        {loading && (
          <p className="text-center text-emerald-50/60 mt-12" data-testid="books-loading">
            Summoning the books…
          </p>
        )}
      </div>
    </section>
  );
};

export default BookSeries;
